import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import APIRequest, { APIError } from "system/APIRequest";
import { mergeQueries, Query, queryOps } from "system/query/Query";
import { useDashboard } from "./Dashboard";
import { assemblePageQueryString, getDashboardNavigatePath, parsePageParams, parsePageQueries } from "components/resources/dashboards/DashboardNavigationUtils";
import { parseTemplateQueryInfo } from "components/resources/dashboards/DashboardDocument";
import { Alert, Box, Button, useTheme } from "@mui/material";
import TopBar from "components/dashboardParts/TopBar";
import WidgetLoader from "components/widgetParts/WidgetLoader";

export interface SelectedTemplateQuery {
  index: number;
  id: string;
  color: string;
  query: Query;
}

export interface TemplateQueryContext {
  datasetId?: string;

  globalQuery?: Query;

  rawTemplateQueries: Array<Query>;

  allTemplateQueries: Array<SelectedTemplateQuery>;
  selectedTemplateQueries: Array<SelectedTemplateQuery>;
  templateQueriesUnion?: Query;
}

// Context to provide the list of template queries to child components.
const TemplateQueriesContext = React.createContext<TemplateQueryContext>({} as TemplateQueryContext);
const TemplateQueriesProvider = TemplateQueriesContext.Provider;

// Custom hook to access the list of template queries in child components.
export const useTemplateQueries = () => {
  const templateQueries = useContext(TemplateQueriesContext);
  if (!templateQueries) {
    throw new Error(
      "useDashboard has to be used within <TemplateQueriesContext.Provider>"
    );
  }
  return templateQueries;
};

export default function DashboardPageRoot(): JSX.Element {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dashboard = useDashboard();
  const theme = useTheme();

  const [ hasTemplateQueries, setHasTemplateQueries] = useState(true);

  const [ hasDeletedQuery, setHasDeletedQuery ] = useState(false);

  const { queryIds, templateQueryIndices } = useParams();
  const [ rawTemplateQueries, setAllTemplateQueries ] = useState<Array<Query>>([]);
  const [ templateQueriesUnion, setTemplateQueriesUnion ] = useState<Query | undefined>(undefined);
  const [ globalQuery, setGlobalQuery ] = useState<Query>();
  const [ templateQueryInfo, setTemplateQueryInfo ] = useState(parseTemplateQueryInfo(queryIds, templateQueryIndices, dashboard));

  const isLoadingQueries = useRef<boolean>(false);

  useEffect(() => {
    const templateQueryInfo = parseTemplateQueryInfo(queryIds, templateQueryIndices, dashboard);

    setTemplateQueryInfo(templateQueryInfo);
    if (templateQueryInfo.templateQueryIds && templateQueryInfo.templateQueryIds.length > 0) {
      setHasTemplateQueries(true);
    } else {
      setHasTemplateQueries(false);
    }
  }, [queryIds, templateQueryIndices]);

  // In a useeffect because the react router doesn't like navigate calls while building a component.
  useEffect(() => {
    const pathParts = parsePageParams();
    // Check if we have indentical queries and if so, redirect to only the unique ones.
    // The query info call will fail with 404 if there are duplicate query IDs passed,
    // and there is no good reason to have duplicate template queries, so we just fix it with the redirect.
    const dashboardQueries = parsePageQueries(pathParts.queryIds);
    const uniqueDashboardQueries = Array.from(new Set(dashboardQueries.queryIds));
    if (uniqueDashboardQueries.length < dashboardQueries.queryIds.length) {
      dashboardQueries.queryIds = uniqueDashboardQueries;
      const pageQueries = assemblePageQueryString(dashboardQueries.queryIds, dashboardQueries.globalQueryId);
      pathParts.queryIds = pageQueries;
      const navigatePath = getDashboardNavigatePath(pathParts);

      // For whatever reason, react does not reload this page's contents and it remains in an error state if we use navigate here.
      window.location.href = navigatePath;
      return;
    }

    // If this is a template dashboard and no template query indices are provided,
    // redirect to either the first template query or the default dashboard query.
    if (dashboard.template_type && (!templateQueryInfo.selectedTemplateQueryIndices || templateQueryInfo.selectedTemplateQueryIndices.length === 0) && templateQueryInfo.templateQueryIds && templateQueryInfo.templateQueryIds.length > 0) {
      pathParts.templateQueryIndices = [0];
      const navigatePath = getDashboardNavigatePath(pathParts);
      navigate(navigatePath, { replace: true });
    } else if (dashboard.template_type && (!templateQueryInfo.templateQueryIds || templateQueryInfo.templateQueryIds.length === 0) && dashboard.data.defaultDashboardQuery) {
      pathParts.templateQueryIndices = [0];
      pathParts.queryIds = dashboard.data.defaultDashboardQuery;
      const navigatePath = getDashboardNavigatePath(pathParts);
      navigate(navigatePath, { replace: true });
    }
  }, [dashboard.template_type, templateQueryInfo.selectedTemplateQueryIndices, templateQueryInfo.templateQueryIds, dashboard.data.defaultDashboardQuery]);

  useEffect(() => {
    // If we are already loading queries or there are no query IDs to load, do not proceed.
    if (isLoadingQueries.current || ((!templateQueryInfo.templateQueryIds || templateQueryInfo.templateQueryIds.length === 0) && !templateQueryInfo.globalQueryId)) {
      return;
    }

    // Load the global query if it exists.
    if (templateQueryInfo.globalQueryId) {
      isLoadingQueries.current = true;
      APIRequest(`obj/query/${templateQueryInfo.globalQueryId}`).then((response: any) => {
        setGlobalQuery(new Query(response.output));
      }).catch((reason: APIError) => {
        enqueueSnackbar(`${i18n.t("api-request.template-queries-error")}\n${reason.server_message}`, { variant: "error", persist: true });
        setHasDeletedQuery(true);
      });
    } else {
      setGlobalQuery(undefined);
    }

    // Load the template queries if they exist.
    if (templateQueryInfo.templateQueryIds && templateQueryInfo.templateQueryIds.length > 0) {
      isLoadingQueries.current = true;
      APIRequest(`obj/query/${templateQueryInfo.templateQueryIds.join(",")}`).then((response: any) => {
        if (!response.output || !Array.isArray(response.output)) {
          response.output = response?.output ? [response?.output] : [];
        }
        const queries:Array<Query> = response.output.map((query: any) => {
          return new Query(query);
        });
        // Sort the queries by their order in the URL.
        queries.sort((a, b) => {
          // These things should not be possible due to earlier checks, but this shuts up TypeScript.
          if (!templateQueryInfo.templateQueryIds || !a.id || !b.id) {
            return 0;
          }
          return templateQueryInfo.templateQueryIds.indexOf(a.id) - templateQueryInfo.templateQueryIds.indexOf(b.id);
        });
        setAllTemplateQueries(queries);
        isLoadingQueries.current = false;
      }).catch((reason: APIError) => {
        isLoadingQueries.current = false;
        enqueueSnackbar(`${i18n.t("api-request.template-queries-error")}\n${reason.server_message}`, { variant: "error", persist: true });
        setHasDeletedQuery(true);
      });
    }
  }, [templateQueryInfo.templateQueryIds, templateQueryInfo.globalQueryId]);

  // Build template query representations of the loaded template queries with consistent colors.
  const allTemplateQueries: Array<SelectedTemplateQuery> = useMemo(() => {
    if (!templateQueryInfo.templateQueryIds || !rawTemplateQueries || rawTemplateQueries.length === 0) {
      return [];
    }
    const convertedQueries: Array<SelectedTemplateQuery> = rawTemplateQueries.map((query, index) => {
      const selectedTemplateQuery:SelectedTemplateQuery = {
        index: index,
        id: query.id || "",
        color: query?.meta?.color || theme.colors.namedColors.chartColorArray[index % theme.colors.namedColors.chartColorArray.length],
        query: query
      };
      return selectedTemplateQuery;
    });
    return convertedQueries;
  }, [templateQueryInfo, templateQueryInfo.templateQueryIds, rawTemplateQueries]);

  // If there are multiple selected queries, we need to assemble an
  // ephemeral query that combines them.
  useEffect(() => {
    if (!templateQueryInfo || !templateQueryInfo.selectedTemplateQueryIndices ||
      templateQueryInfo.selectedTemplateQueryIndices.length < 2 || allTemplateQueries.length < 2) {
      setTemplateQueriesUnion(allTemplateQueries?.[0]?.query || undefined);
      return;
    }

    // Get the selected template queries.
    const selectedQueries: Array<Query> = [];
    templateQueryInfo.selectedTemplateQueryIndices.forEach((index) => {
      const selectedTemplateQuery = allTemplateQueries.find(tq => tq.index === index);
      if (selectedTemplateQuery) {
        selectedQueries.push(selectedTemplateQuery.query);
      }
    });

    const templateQueriesUnion = mergeQueries(selectedQueries, { op: queryOps.or });
    templateQueriesUnion.name = i18n.t("dashboard.all-template-queries");
    templateQueriesUnion.is_ephemeral = true;

    APIRequest("obj/query", {}, {
      method: "POST",
      "body": JSON.stringify(templateQueriesUnion, undefined, 2)
    }).then((response: any) => {
      templateQueriesUnion.id = response.id;
      setTemplateQueriesUnion(templateQueriesUnion);
    }).catch((error: APIError) => {
      enqueueSnackbar(`${i18n.t("api-request.template-queries-error")}\n${error.server_message}`, { variant: "error", persist: true });
    });
  }, [templateQueryInfo.selectedTemplateQueryIndices, allTemplateQueries]);

  const selectedTemplateQueries: Array<SelectedTemplateQuery> = useMemo(() => {
    if (!allTemplateQueries || allTemplateQueries.length === 0 ||
      !templateQueryInfo.selectedTemplateQueryIndices || templateQueryInfo.selectedTemplateQueryIndices.length === 0) {
      return [];
    }
    const selectedQueries: Array<SelectedTemplateQuery> = templateQueryInfo.selectedTemplateQueryIndices.map((index) => {
      return allTemplateQueries[index];
    });
    return selectedQueries;
  }, [templateQueryInfo, templateQueryInfo.templateQueryIds, templateQueryInfo.selectedTemplateQueryIndices, allTemplateQueries]);

  const templateQueriesLoaded = useMemo(() => {
    // If there are no template queries, consider them loaded.
    if (!hasTemplateQueries) {
      return true;
    }
    if (templateQueriesUnion && allTemplateQueries && allTemplateQueries.length > 0) {
      return true;
    }
    return false;
  }, [hasTemplateQueries, templateQueryInfo, allTemplateQueries, templateQueriesUnion]);

  const templateQueryContext = useMemo(() => {
    return {
      datasetId: dashboard.dataset_id || undefined,
      rawTemplateQueries: rawTemplateQueries,
      globalQuery: globalQuery,
      allTemplateQueries: allTemplateQueries,
      selectedTemplateQueries: selectedTemplateQueries,
      templateQueriesUnion: templateQueriesUnion
    }
  }, [dashboard, rawTemplateQueries, globalQuery, allTemplateQueries, selectedTemplateQueries, templateQueriesUnion]);

  return (
    <>
    {hasDeletedQuery &&
      <Box sx={{ maxWidth:"1300px", margin:"0 auto", position:"relative", pl: 2, pr: 2 }}>
        <TopBar showLogo={true} ></TopBar>
        <Alert severity="warning" sx={{ m: 5 }}>
          {i18n.t("dashboard.errors.query-not-found-warning")}
          <br /> <br />
          <Button variant="contained" onClick={() => navigate("/start")}>{i18n.t("dashboard.errors.return-to-homepage")}</Button>
        </Alert>
      </Box>
    }
    {(hasTemplateQueries && !hasDeletedQuery) &&
      <>
        {!!allTemplateQueries.length && templateQueriesLoaded &&
          <TemplateQueriesProvider value={templateQueryContext}>
            <Outlet/>
          </TemplateQueriesProvider>
        }
        {!allTemplateQueries.length &&
          // Show loading
          <Box sx={{ maxWidth:"1300px", margin:"0 auto", position:"relative", pl: 2, pr: 2, textAlign: "center" }}>
            <img src='/img/Infegy_Logo_Loader.svg' alt='Loading...' style={{ width: '200px', opacity: 0.3, margin: "0 auto" }} />
          </Box>
        }
      </>
    }
    {!hasTemplateQueries &&
      <TemplateQueriesProvider
        value={{
          datasetId: dashboard.dataset_id || undefined,
          rawTemplateQueries: [],
          globalQuery: globalQuery,
          allTemplateQueries: [],
          selectedTemplateQueries: [],
          templateQueriesUnion: undefined
        }}>
        <Outlet/>
      </TemplateQueriesProvider>
    }
    </>
  );
}